import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position } from '@xyflow/react';
import { Image, Popover, message } from 'antd';
import { CaretDownOutlined, DownloadOutlined } from '@ant-design/icons';
import AddNodePopover, { FUNCTION_LABELS, FUNCTION_NODE_GROUPS } from '../../components/AddNodePopover';
import { getPanelComponent } from '../../panels';
import { downloadFlowOutputImage } from '../../execution';
import Utils from '@utils/utils';
import styles from './index.module.scss';

const PLACEHOLDER_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn//photostudio-opshub/FreeCanvas/node-image.svg';
const HANDLE_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn//photostudio-opshub/FreeCanvas/menu.svg';
const PROCESSING_VIDEO = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-web/admin/images_v3/picProcessingBg.webm';
const DOWNLOAD_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn//photostudio-opshub/FreeCanvas/download.svg';

const LEFT_HANDLE_CX = -22;
const RIGHT_HANDLE_CX = 262;
const HANDLE_CY = 160;

const MAGNET_RADIUS = 80;
const MAX_PULL = 18;
const TITLE_MAX_LENGTH = 10;
const TITLE_PATTERN = /^[A-Za-z0-9\u4e00-\u9fa5]+$/;

const computeHandleOffset = (mx, my, cx, cy, side) => {
    const dx = mx - cx;
    const dy = my - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist >= MAGNET_RADIUS || dist < 0.5) return { x: 0, y: 0 };

    const pull = MAX_PULL * (1 - dist / MAGNET_RADIUS);
    let offsetX = (dx / dist) * pull;
    const offsetY = (dy / dist) * pull;

    if (side === 'left' && offsetX > 0) offsetX = 0;
    if (side === 'right' && offsetX < 0) offsetX = 0;

    return { x: offsetX, y: offsetY };
};

const HANDLE_BASE_STYLE = {
    position: 'absolute',
    width: 36,
    height: 36,
    background: 'transparent',
    border: 'none',
    borderRadius: 0,
    top: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 36,
    minHeight: 36,
    transition: 'opacity 0.16s ease, transform 0.16s ease',
};

const MIME_EXTENSION_MAP = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/bmp': 'bmp',
    'image/svg+xml': 'svg',
};

const getExtensionFromBlob = (blob, fallbackUrl = '') => {
    const mimeExtension = MIME_EXTENSION_MAP[blob?.type];
    if (mimeExtension) return mimeExtension;

    const urlPath = fallbackUrl.split('?')[0] || '';
    const lastSegment = urlPath.split('/').pop() || '';
    const urlExtension = lastSegment.includes('.') ? lastSegment.split('.').pop() : '';
    return urlExtension || 'png';
};

const getDownloadFileName = (url, blob, prefix = 'result-image') => {
    const urlPath = url.split('?')[0] || '';
    const lastSegment = urlPath.split('/').pop() || '';
    const hasExtension = /\.[a-zA-Z0-9]+$/.test(lastSegment);

    if (lastSegment && hasExtension) {
        return lastSegment;
    }

    return `${prefix}-${Date.now()}.${getExtensionFromBlob(blob, url)}`;
};

const normalizeResultItems = (outputs = []) => outputs
    .filter((item) => item?.type === 'image' && item?.url)
    .map((item, index) => ({
        id: item.id || `result-${index + 1}`,
        primary: !!item.primary,
        tag: item.primary ? '主图' : `结果${index + 1}`,
        title: item.key || `结果 ${index + 1}`,
        src: Utils.getImgThumbnail(item.url),
        originalUrl: item.url,
    }));

const buildOrderedResults = (items, activeIndex) => items.map((item, index) => ({
    ...item,
    index,
    layerOrder: index === activeIndex ? items.length - 1 : (
        index < activeIndex ? index : index - 1
    ),
}));

const ResultArtwork = ({ src, title }) => (
    <div className={styles.resultArtwork}>
        <img src={src} alt={title} className={styles.resultImage} draggable={false} />
    </div>
);

const getGalleryPosition = (wrapperElement) => {
    if (!wrapperElement) return null;

    const rect = wrapperElement.getBoundingClientRect();
    return {
        top: rect.top,
        left: rect.right + 18,
    };
};

const normalizeUrlList = (value) => {
    if (Array.isArray(value)) {
        return value.filter(Boolean);
    }

    return [];
};

const collectCurrentManualImageUrls = (data) => {
    const panelState = data?.panelState || {};

    if (Array.isArray(panelState.manualUploads)) {
        return panelState.manualUploads.filter(Boolean);
    }

    const currentUploads = getCurrentUploadMap(data);
    const currentLinkedSlotSources = getCurrentLinkedSlotSourceMap(data);

    return ['source', 'commodity', 'reference', 'match', 'otherView1', 'otherView2']
        .filter((slotKey) => !currentLinkedSlotSources?.[slotKey]?.edgeId)
        .map((slotKey) => currentUploads?.[slotKey] || '')
        .filter(Boolean);
};

const getCurrentUploadMap = (data) => {
    const panelStateUploads = data?.panelState?.uploads;
    const inputParams = data?.inputParams || {};

    if (panelStateUploads && typeof panelStateUploads === 'object' && !Array.isArray(panelStateUploads)) {
        return {
            source: panelStateUploads.source || '',
            commodity: panelStateUploads.commodity || '',
            reference: panelStateUploads.reference || '',
            match: panelStateUploads.match || '',
            otherView1: panelStateUploads.otherView1 || '',
            otherView2: panelStateUploads.otherView2 || '',
        };
    }

    return {
        source: inputParams.srcImage || '',
        commodity: inputParams.commodityImage || '',
        reference: inputParams.refImage || inputParams.commodityImage || '',
        match: inputParams.matchImage || '',
        otherView1: inputParams.angle1 || inputParams.otherView1Image || '',
        otherView2: inputParams.angle2 || inputParams.otherView2Image || '',
    };
};

const getCurrentLinkedSlotSourceMap = (data) => {
    const linkedSlotSources = data?.panelState?.linkedSlotSources;

    if (!linkedSlotSources || typeof linkedSlotSources !== 'object' || Array.isArray(linkedSlotSources)) {
        return {};
    }

    return {
        source: linkedSlotSources.source || null,
        commodity: linkedSlotSources.commodity || null,
        reference: linkedSlotSources.reference || null,
        match: linkedSlotSources.match || null,
        otherView1: linkedSlotSources.otherView1 || null,
        otherView2: linkedSlotSources.otherView2 || null,
    };
};

const pickLinkedSlotSourceMap = (slotKeyMap, currentLinkedSlotSources) => Object.entries(slotKeyMap).reduce((acc, [targetSlotKey, semanticKey]) => {
    if (!semanticKey || !currentLinkedSlotSources?.[semanticKey]) return acc;

    return {
        ...acc,
        [targetSlotKey]: currentLinkedSlotSources[semanticKey],
    };
}, {});

const collectCurrentLinkedUploadItems = (data) => {
    const currentPanelState = data?.panelState || {};
    const currentLinkedSlotSources = getCurrentLinkedSlotSourceMap(data);
    const linkedUploads = Array.isArray(currentPanelState.linkedUploads)
        ? currentPanelState.linkedUploads
        : [];
    const orderedLinkedItems = [
        currentLinkedSlotSources.source,
        currentLinkedSlotSources.reference,
        ...linkedUploads,
    ].filter((item) => !!item?.edgeId);
    const seenEdgeIds = new Set();

    return orderedLinkedItems.filter((item) => {
        const edgeId = String(item?.edgeId || '');
        if (!edgeId || seenEdgeIds.has(edgeId)) return false;
        seenEdgeIds.add(edgeId);
        return true;
    });
};

const pickUploadsBySlotMap = (slotKeyMap, currentUploads) => Object.entries(slotKeyMap).reduce((acc, [targetSlotKey, semanticKey]) => {
    if (!semanticKey) return acc;

    return {
        ...acc,
        [targetSlotKey]: currentUploads?.[semanticKey] || '',
    };
}, {});

const getCurrentNumericValue = (data, key, fallback) => (
    data?.inputParams?.[key]
    ?? data?.panelState?.extraSelectValues?.[key]
    ?? data?.panelState?.extraSwitchValues?.[key]
    ?? fallback
);

const DEFAULT_SWITCH_GENERATE_COUNT = 4;
const DEFAULT_MATERIAL_HD_GENERATE_COUNT = 1;

const buildSwitchFunctionPayload = (data, nextKey) => {
    const currentInputParams = data?.inputParams || {};
    const currentPanelState = data?.panelState || {};
    const currentUploads = getCurrentUploadMap(data);
    const currentLinkedSlotSources = getCurrentLinkedSlotSourceMap(data);

    switch (nextKey) {
        case 'templateReplace': {
            const nextReferenceUploads = normalizeUrlList(collectCurrentManualImageUrls(data)).slice(0, 13);
            const nextUploads = ['', ...nextReferenceUploads].slice(0, 14);
            const prompt = currentInputParams.prompt ?? currentPanelState.prompt ?? '';
            const model = currentInputParams.model ?? currentPanelState.model ?? 'NanoPro';
            const aspectRatio = currentInputParams.aspectRatio ?? currentPanelState.aspectRatio ?? '3:4';
            const imageSize = currentInputParams.imageSize ?? currentPanelState.imageSize ?? '4K';
            const num = getCurrentNumericValue(data, 'num', 1);
            const linkedUploadItems = collectCurrentLinkedUploadItems(data);
            const primaryReferenceLinkedItem = linkedUploadItems[0] || null;
            const nextLinkedSlotSources = primaryReferenceLinkedItem
                ? { reference: primaryReferenceLinkedItem }
                : {};
            const linkedUploads = linkedUploadItems.slice(1);

            return {
                inputParams: {
                    imageUrls: nextUploads,
                    srcImage: '',
                    refImage: nextReferenceUploads[0] || '',
                    prompt,
                    model,
                    aspectRatio,
                    imageSize,
                    num,
                    skipSelect: 0,
                },
                panelState: {
                    manualUploads: nextUploads,
                    linkedUploads,
                    linkedSlotSources: nextLinkedSlotSources,
                    uploads: nextUploads,
                    prompt,
                    model,
                    aspectRatio,
                    imageSize,
                    num,
                    skipSelect: false,
                },
            };
        }
        case 'templateReplaceExpt':
        case 'faceSwap': {
            const isFaceSwap = nextKey === 'faceSwap';
            const slotKeyMap = isFaceSwap
                ? { source: 'source', reference: 'reference' }
                : { source: 'source', reference: 'reference', match: 'match' };
            const nextUploads = pickUploadsBySlotMap(slotKeyMap, currentUploads);
            const nextInputParams = Object.keys(slotKeyMap).reduce((acc, key) => {
                const paramKey = ({
                    source: 'srcImage',
                    reference: 'refImage',
                    match: 'matchImage',
                })[key];

                return {
                    ...acc,
                    [paramKey]: nextUploads[key] || '',
                };
            }, {
                num: DEFAULT_SWITCH_GENERATE_COUNT,
            });

            if (isFaceSwap) {
                nextInputParams.orientation = 'front';
                nextInputParams.keepHat = 0;
                nextInputParams.emoji = 'src';
            }

            return {
                inputParams: nextInputParams,
                panelState: {
                    uploads: nextUploads,
                    linkedSlotSources: pickLinkedSlotSourceMap(slotKeyMap, currentLinkedSlotSources),
                    advancedOpen: false,
                    ...(isFaceSwap ? {
                        extraSelectValues: {
                            orientation: nextInputParams.orientation,
                            emoji: nextInputParams.emoji,
                        },
                        extraSwitchValues: {
                            keepHat: nextInputParams.keepHat,
                        },
                    } : {}),
                    num: nextInputParams.num,
                },
            };
        }
        case 'materialHD': {
            const slotKeyMap = { reference: 'reference', source: 'source' };
            const nextUploads = pickUploadsBySlotMap(slotKeyMap, currentUploads);
            const fastMode = 1;
            const clothType = 0;
            const mode = 0;

            return {
                inputParams: {
                    srcImage: nextUploads.source || '',
                    refImage: nextUploads.reference || '',
                    mode,
                    clothType,
                    fastMode,
                    num: DEFAULT_MATERIAL_HD_GENERATE_COUNT,
                },
                panelState: {
                    uploads: nextUploads,
                    linkedSlotSources: pickLinkedSlotSourceMap(slotKeyMap, currentLinkedSlotSources),
                    advancedOpen: false,
                    mode,
                    clothType,
                    extraSwitchValues: { fastMode },
                    num: DEFAULT_MATERIAL_HD_GENERATE_COUNT,
                },
            };
        }
        case 'skinHD': {
            const slotKeyMap = { source: 'source' };
            const nextUploads = pickUploadsBySlotMap(slotKeyMap, currentUploads);
            const hd_area = 0;
            const mode = 0;
            const shine = 0;

            return {
                inputParams: {
                    srcImage: nextUploads.source || '',
                    hd_area,
                    mode,
                    shine,
                    num: DEFAULT_SWITCH_GENERATE_COUNT,
                },
                panelState: {
                    uploads: nextUploads,
                    linkedSlotSources: pickLinkedSlotSourceMap(slotKeyMap, currentLinkedSlotSources),
                    advancedOpen: false,
                    extraSelectValues: { hd_area, mode },
                    extraSwitchValues: { shine },
                    num: DEFAULT_SWITCH_GENERATE_COUNT,
                },
            };
        }
        case 'imageUpscale': {
            const slotKeyMap = { source: 'source' };
            const fromCreativeImg2Img = data?.functionKey === 'creativeImg2Img';
            const creativeManualUploads = fromCreativeImg2Img
                ? normalizeUrlList(collectCurrentManualImageUrls(data))
                : [];
            const nextUploads = fromCreativeImg2Img
                ? { source: creativeManualUploads[0] || '' }
                : pickUploadsBySlotMap(slotKeyMap, currentUploads);
            const nextLinkedSlotSources = fromCreativeImg2Img
                ? {}
                : pickLinkedSlotSourceMap(slotKeyMap, currentLinkedSlotSources);
            const mode = 0;

            return {
                inputParams: {
                    srcImage: nextUploads.source || '',
                    mode,
                    num: DEFAULT_SWITCH_GENERATE_COUNT,
                },
                panelState: {
                    uploads: nextUploads,
                    linkedSlotSources: nextLinkedSlotSources,
                    advancedOpen: false,
                    extraSwitchValues: { mode },
                    num: DEFAULT_SWITCH_GENERATE_COUNT,
                },
            };
        }
        case 'creativeImg2Img': {
            const nextUploads = normalizeUrlList(collectCurrentManualImageUrls(data));
            const prompt = currentInputParams.prompt ?? currentPanelState.prompt ?? '';
            const model = currentInputParams.model ?? currentPanelState.model ?? 'NanoPro';
            const aspectRatio = currentInputParams.aspectRatio ?? currentPanelState.aspectRatio ?? '3:4';
            const imageSize = currentInputParams.imageSize ?? currentPanelState.imageSize ?? '4K';
            const num = getCurrentNumericValue(data, 'num', 1);
            const linkedUploads = collectCurrentLinkedUploadItems(data);
            const ignoredLinkedSourceNodeIds = Array.isArray(currentPanelState.ignoredLinkedSourceNodeIds)
                ? currentPanelState.ignoredLinkedSourceNodeIds
                : [];

            return {
                inputParams: {
                    imageUrls: nextUploads,
                    prompt,
                    model,
                    aspectRatio,
                    imageSize,
                    num,
                },
                panelState: {
                    manualUploads: nextUploads,
                    linkedUploads,
                    ignoredLinkedSourceNodeIds,
                    uploads: nextUploads,
                    prompt,
                    model,
                    aspectRatio,
                    imageSize,
                    num,
                },
            };
        }
        case 'creativeTxt2Img': {
            const prompt = currentInputParams.prompt ?? currentPanelState.prompt ?? '';
            const model = currentInputParams.model ?? currentPanelState.model ?? 'NanoPro';
            const aspectRatio = currentInputParams.aspectRatio ?? currentPanelState.aspectRatio ?? '3:4';
            const imageSize = currentInputParams.imageSize ?? currentPanelState.imageSize ?? '4K';
            const num = getCurrentNumericValue(data, 'num', 1);

            return {
                inputParams: {
                    prompt,
                    model,
                    aspectRatio,
                    imageSize,
                    num,
                },
                panelState: {
                    prompt,
                    model,
                    aspectRatio,
                    imageSize,
                    num,
                },
            };
        }
        case 'shoesTryon': {
            const slotKeyMap = {
                source: 'source',
                reference: 'reference',
                otherView1: 'otherView1',
                otherView2: 'otherView2',
            };
            const nextUploads = pickUploadsBySlotMap(slotKeyMap, currentUploads);

            return {
                inputParams: {
                    srcImage: nextUploads.source || '',
                    refImage: nextUploads.reference || '',
                    angle1: nextUploads.otherView1 || '',
                    angle2: nextUploads.otherView2 || '',
                    num: 1,
                    skipSelect: 0,
                },
                panelState: {
                    uploads: nextUploads,
                    linkedSlotSources: pickLinkedSlotSourceMap(slotKeyMap, currentLinkedSlotSources),
                    advancedOpen: false,
                    skipSelect: false,
                    num: 1,
                },
            };
        }
        case 'shoesRefiner': {
            const slotKeyMap = {
                source: 'source',
                commodity: 'commodity',
                otherView1: 'otherView1',
                otherView2: 'otherView2',
            };
            const nextUploads = pickUploadsBySlotMap(slotKeyMap, currentUploads);

            return {
                inputParams: {
                    srcImage: nextUploads.source || '',
                    commodityImage: nextUploads.commodity || '',
                    refImage: '',
                    angle1: nextUploads.otherView1 || '',
                    angle2: nextUploads.otherView2 || '',
                    num: 1,
                    skipSelect: 0,
                },
                panelState: {
                    uploads: nextUploads,
                    linkedSlotSources: pickLinkedSlotSourceMap(slotKeyMap, currentLinkedSlotSources),
                    advancedOpen: false,
                    skipSelect: false,
                    num: 1,
                },
            };
        }
        case 'repose': {
            const slotKeyMap = {
                source: 'source',
                reference: 'reference',
            };
            const nextUploads = pickUploadsBySlotMap(slotKeyMap, currentUploads);

            return {
                inputParams: {
                    srcImage: nextUploads.source || '',
                    refImage: nextUploads.reference || '',
                    mode: 'ref',
                    prompt: '',
                    num: 4,
                    skipSelect: 0,
                },
                panelState: {
                    uploads: nextUploads,
                    linkedSlotSources: pickLinkedSlotSourceMap(slotKeyMap, currentLinkedSlotSources),
                    advancedOpen: false,
                    skipSelect: false,
                    mode: 'ref',
                    prompt: '',
                    num: 4,
                },
            };
        }
        case 'relight': {
            const slotKeyMap = {
                source: 'source',
                reference: 'reference',
                match: 'match',
            };
            const fromCreativeImg2Img = data?.functionKey === 'creativeImg2Img';
            const creativeManualUploads = fromCreativeImg2Img
                ? normalizeUrlList(collectCurrentManualImageUrls(data))
                : [];
            const nextUploads = fromCreativeImg2Img
                ? {
                    source: creativeManualUploads[0] || '',
                    reference: creativeManualUploads[1] || '',
                    match: creativeManualUploads[2] || '',
                }
                : pickUploadsBySlotMap(slotKeyMap, currentUploads);
            const nextLinkedSlotSources = fromCreativeImg2Img
                ? {}
                : pickLinkedSlotSourceMap(slotKeyMap, currentLinkedSlotSources);

            return {
                inputParams: {
                    srcImage: nextUploads.source || '',
                    refImage: nextUploads.reference || '',
                    matchImage: nextUploads.match || '',
                    mode: 'single',
                    targetArea: 0,
                    num: 4,
                    skipSelect: 0,
                },
                panelState: {
                    uploads: nextUploads,
                    linkedSlotSources: nextLinkedSlotSources,
                    skipSelect: false,
                    mode: 'single',
                    targetArea: 0,
                    num: 4,
                },
            };
        }
        default:
            return {
                inputParams: {},
                panelState: {},
            };
    }
};

const FunctionNode = ({ id, data, selected, dragging }) => {
    const wrapperRef = useRef(null);
    const hideTimerRef = useRef(null);
    const suppressAutoPanelRef = useRef(false);
    const reopenOverlayRef = useRef('');
    const [leftOffset, setLeftOffset] = useState({ x: 0, y: 0 });
    const [rightOffset, setRightOffset] = useState({ x: 0, y: 0 });
    const [hovered, setHovered] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);
    const [activeResultIndex, setActiveResultIndex] = useState(0);
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [galleryPosition, setGalleryPosition] = useState(null);
    const [previewImageIndex, setPreviewImageIndex] = useState(-1);
    const [editingSequenceLabel, setEditingSequenceLabel] = useState(false);
    const [draftSequenceLabel, setDraftSequenceLabel] = useState(data.imageSequenceLabel || '');
    const sequenceInputRef = useRef(null);
    const dismissTokenRef = useRef(data.panelDismissToken);

    const showHandles = hovered || !!selected;
    const PanelComponent = useMemo(() => getPanelComponent(data.functionKey), [data.functionKey]);
    const executionStatus = data.executionStatus || data.latestExecution?.status || 'idle';
    const interactionLocked = !!data.interactionLocked;
    const groupExecutionLocked = !!data.groupExecutionLocked;
    const readOnlyView = !!data.readOnlyView;
    const viewportInteracting = !!data.viewportInteracting;
    const resultItems = useMemo(
        () => normalizeResultItems(data.latestExecution?.outputs || []),
        [data.latestExecution?.outputs],
    );
    const displayMode = resultItems.length > 0 ? 'stacked' : 'empty';
    const isManualSelectionPending = executionStatus === 'manual';
    const selectedNodeCount = data.selectedNodeCount || 0;
    const isSingleSelected = selected && selectedNodeCount === 1;
    const orderedResults = useMemo(
        () => buildOrderedResults(resultItems, activeResultIndex),
        [activeResultIndex, resultItems],
    );
    const activeResult = orderedResults.find((item) => item.index === activeResultIndex) || orderedResults[0];
    const selectedResultIndex = useMemo(
        () => resultItems.findIndex((item) => String(item.id) === String(data.selectedOutputId ?? '')),
        [data.selectedOutputId, resultItems],
    );
    const resolvedSelectedResultIndex = useMemo(() => {
        if (selectedResultIndex >= 0) return selectedResultIndex;
        const primaryResultIndex = resultItems.findIndex((item) => !!item.primary);
        if (primaryResultIndex >= 0) return primaryResultIndex;
        return resultItems.length > 0 ? 0 : -1;
    }, [resultItems, selectedResultIndex]);
    const stackedBackResults = orderedResults
        .filter((item) => item.index !== activeResultIndex)
        .sort((a, b) => a.layerOrder - b.layerOrder);
    const galleryColumnCount = orderedResults.length <= 1
        ? 1
        : orderedResults.length <= 4
            ? 2
            : Math.min(5, orderedResults.length);
    const displaySequenceLabel = data.imageSequenceLabel || '';

    useEffect(() => {
        setDraftSequenceLabel(displaySequenceLabel);
    }, [displaySequenceLabel]);

    useEffect(() => {
        if (!editingSequenceLabel) return;
        sequenceInputRef.current?.focus();
        sequenceInputRef.current?.select();
    }, [editingSequenceLabel]);

    const handleEnter = useCallback(() => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
        setHovered(true);
    }, []);

    const handleLeave = useCallback(() => {
        hideTimerRef.current = setTimeout(() => {
            setHovered(false);
            setLeftOffset({ x: 0, y: 0 });
            setRightOffset({ x: 0, y: 0 });
            hideTimerRef.current = null;
        }, 120);
    }, []);

    const handleMouseMove = useCallback((event) => {
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0) return;

        const scale = rect.width / 240;
        const mx = (event.clientX - rect.left) / scale;
        const my = (event.clientY - rect.top) / scale;

        setLeftOffset(computeHandleOffset(mx, my, LEFT_HANDLE_CX, HANDLE_CY, 'left'));
        setRightOffset(computeHandleOffset(mx, my, RIGHT_HANDLE_CX, HANDLE_CY, 'right'));

        const dxL = mx - LEFT_HANDLE_CX;
        const dyL = my - HANDLE_CY;
        const dxR = mx - RIGHT_HANDLE_CX;
        const dyR = my - HANDLE_CY;
        const inMagnet = (
            Math.sqrt(dxL * dxL + dyL * dyL) < MAGNET_RADIUS
            || Math.sqrt(dxR * dxR + dyR * dyR) < MAGNET_RADIUS
        );

        if (inMagnet) {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
            setHovered(true);
        }
    }, []);

    const handleClassName = `${styles.handle} ${!showHandles ? styles.handleHidden : ''}`;

    const handleLabelChange = useCallback((key) => {
        if (executionStatus === 'processing' || interactionLocked || key === data.functionKey) return;

        const nextPayload = buildSwitchFunctionPayload(data, key);

        const nextPatch = {
            label: FUNCTION_LABELS[key],
            functionKey: key,
            ...nextPayload,
        };

        if (typeof data.onSwitchFunction === 'function') {
            data.onSwitchFunction(id, nextPatch);
        } else {
            data.onChange?.(id, nextPatch);
        }
        reopenOverlayRef.current = !!getPanelComponent(key) ? 'panel' : '';
        setGalleryOpen(false);
        setPanelOpen(!!getPanelComponent(key));
    }, [data, executionStatus, id, interactionLocked]);

    const handlePanelClose = useCallback((event) => {
        event?.stopPropagation?.();
        suppressAutoPanelRef.current = true;
        reopenOverlayRef.current = '';
        setPanelOpen(false);
    }, []);

    const handleCardClick = useCallback(() => {
        if (readOnlyView) return;
        reopenOverlayRef.current = 'panel';
        setGalleryOpen(false);
        if (!PanelComponent) return;
        setPanelOpen(true);
    }, [PanelComponent, readOnlyView]);

    const handleGalleryToggle = useCallback((event) => {
        event.stopPropagation();
        setPanelOpen(false);
        setGalleryOpen((prev) => {
            const nextOpen = !prev;
            reopenOverlayRef.current = nextOpen ? 'gallery' : '';
            return nextOpen;
        });
    }, []);

    const handleManualSelectClick = useCallback((event) => {
        event.stopPropagation();
        if (readOnlyView) return;
        reopenOverlayRef.current = 'gallery';
        setPanelOpen(false);
        setGalleryOpen(true);
    }, [readOnlyView]);

    const handleResultSelect = useCallback((event, resultIndex) => {
        event.stopPropagation();
        setActiveResultIndex(resultIndex);
        reopenOverlayRef.current = '';
        setGalleryOpen(false);
        data.onResultSelect?.(id, resultItems[resultIndex]?.id);
    }, [data, id, resultItems]);

    // 优先通过接口获取下载地址，失败时降级为直接拉取 URL
    const downloadResultByOutputId = useCallback(async (outputId, fallbackUrl) => {
        const projectId = data.projectId;
        const url = fallbackUrl || '';
        if (!outputId && !url) return;

        // outputId 是服务端真实 ID（非 fallback 的 result-N 形式）时才调接口
        const isRealOutputId = outputId && !String(outputId).startsWith('result-');
        let downloadUrl = url;

        if (isRealOutputId && projectId) {
            try {
                const apiUrl = await downloadFlowOutputImage(projectId, outputId);
                if (apiUrl) downloadUrl = apiUrl;
            } catch {
                // 接口失败，降级为直接使用原 URL
            }
        }

        if (!downloadUrl) return;

        try {
            const response = await fetch(downloadUrl);
            const blob = await response.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = getDownloadFileName(downloadUrl, blob);
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (error) {
            window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        }
    }, [data.projectId]);

    const handleDownloadResult = useCallback((event, outputId, fallbackUrl) => {
        event.stopPropagation();
        void downloadResultByOutputId(outputId, fallbackUrl);
    }, [downloadResultByOutputId]);

    const handlePreviewResult = useCallback((event, resultIndex) => {
        event.stopPropagation();
        setPreviewImageIndex(resultIndex);
    }, []);

    const renderPreviewToolbar = useCallback((originalNode, info) => {
        if (!React.isValidElement(originalNode)) return originalNode;

        const currentIndex = typeof info?.current === 'number'
            ? info.current
            : (previewImageIndex >= 0 ? previewImageIndex : 0);
        const currentPreviewUrl = orderedResults[currentIndex]?.originalUrl || '';
        const currentResultId = orderedResults[currentIndex]?.id || '';
        const currentResultSelected = currentIndex === resolvedSelectedResultIndex;
        const originalChildren = React.Children.toArray(originalNode.props.children);
        const filteredChildren = originalChildren.slice(4);

        filteredChildren.push(
            <li
                key="download-current"
                className={`ant-image-preview-operations-operation ${styles.previewDownloadOperation}`}
            >
                <button
                    type="button"
                    className={styles.previewDownloadButton}
                    aria-label="下载当前图片"
                    disabled={!currentPreviewUrl}
                    onClick={(event) => {
                        event.stopPropagation();
                        void downloadResultByOutputId(
                            orderedResults[currentIndex]?.id,
                            currentPreviewUrl,
                        );
                    }}
                >
                    <DownloadOutlined />
                </button>
            </li>,
        );

        if (!readOnlyView) {
            filteredChildren.push(
                <li
                    key="select-current"
                    className={`ant-image-preview-operations-operation ${styles.previewSelectOperation}`}
                >
                    <button
                        type="button"
                        className={`${styles.previewSelectButton} ${currentResultSelected ? styles.previewSelectButtonActive : ''}`}
                        aria-label="选中当前图片"
                        onClick={(event) => {
                            event.stopPropagation();
                            data.onResultSelect?.(id, currentResultId);
                            setActiveResultIndex(currentIndex);
                        }}
                    >
                        {currentResultSelected ? '已选中' : '选这张'}
                    </button>
                </li>,
            );
        }

        return React.cloneElement(originalNode, undefined, filteredChildren);
    }, [data, downloadResultByOutputId, id, orderedResults, previewImageIndex, readOnlyView, resolvedSelectedResultIndex]);

    const handleConnectionHandleMouseDown = useCallback(() => {
        suppressAutoPanelRef.current = true;
    }, []);

    const commitSequenceLabelChange = useCallback(() => {
        const nextLabel = String(draftSequenceLabel || '').trim();
        setEditingSequenceLabel(false);

        if (readOnlyView || interactionLocked) {
            setDraftSequenceLabel(displaySequenceLabel);
            return;
        }

        if (!nextLabel || nextLabel === displaySequenceLabel) {
            setDraftSequenceLabel(displaySequenceLabel);
            return;
        }

        if (nextLabel.length > TITLE_MAX_LENGTH || !TITLE_PATTERN.test(nextLabel)) {
            message.warning('仅支持中文、英文和数字，最长10个字符');
            setDraftSequenceLabel(displaySequenceLabel);
            return;
        }

        data.onChange?.(id, {
            imageSequenceLabel: nextLabel,
        });
    }, [data, displaySequenceLabel, draftSequenceLabel, id, interactionLocked, readOnlyView]);

    const handleSequenceLabelKeyDown = useCallback((event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commitSequenceLabelChange();
            return;
        }

        if (event.key === 'Escape') {
            setEditingSequenceLabel(false);
            setDraftSequenceLabel(displaySequenceLabel);
        }
    }, [commitSequenceLabelChange, displaySequenceLabel]);

    const handleLeftCaptureMouseDown = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    useEffect(() => {
        if (!isSingleSelected) setPanelOpen(false);
    }, [isSingleSelected]);

    useEffect(() => {
        if (dismissTokenRef.current === data.panelDismissToken) return;
        dismissTokenRef.current = data.panelDismissToken;
        reopenOverlayRef.current = '';
        setPanelOpen(false);
        setGalleryOpen(false);
    }, [data.panelDismissToken]);

    useEffect(() => {
        if (dragging || viewportInteracting) {
            setGalleryOpen(false);
            setPanelOpen(false);
        } else if (isSingleSelected) {
            if (suppressAutoPanelRef.current) {
                suppressAutoPanelRef.current = false;
                return;
            }
            if (reopenOverlayRef.current === 'gallery' && displayMode === 'stacked') {
                setGalleryOpen(true);
                return;
            }
            if (reopenOverlayRef.current === 'panel' || !reopenOverlayRef.current) {
                setPanelOpen(true);
            }
        }
    }, [displayMode, dragging, isSingleSelected, viewportInteracting]);

    useEffect(() => {
        if (displayMode !== 'stacked') {
            if (reopenOverlayRef.current === 'gallery') {
                reopenOverlayRef.current = '';
            }
            setGalleryOpen(false);
        }
    }, [displayMode]);

    useEffect(() => {
        if (galleryOpen) {
            setPanelOpen(false);
        }
    }, [galleryOpen]);

    useEffect(() => {
        if (!galleryOpen) return undefined;

        const updateGalleryPosition = () => {
            setGalleryPosition(getGalleryPosition(wrapperRef.current));
        };

        updateGalleryPosition();
        window.addEventListener('resize', updateGalleryPosition);
        window.addEventListener('scroll', updateGalleryPosition, true);

        return () => {
            window.removeEventListener('resize', updateGalleryPosition);
            window.removeEventListener('scroll', updateGalleryPosition, true);
        };
    }, [galleryOpen]);

    useEffect(() => {
        if (activeResultIndex >= orderedResults.length) {
            setActiveResultIndex(0);
        }
    }, [activeResultIndex, orderedResults.length]);

    useEffect(() => {
        if (!resultItems.length) return;

        const selectedIndex = resultItems.findIndex((item) => (
            String(item.id) === String(data.selectedOutputId ?? '')
        ));

        if (selectedIndex >= 0 && selectedIndex !== activeResultIndex) {
            setActiveResultIndex(selectedIndex);
        }
    }, [activeResultIndex, data.selectedOutputId, resultItems]);

    useEffect(() => {
        if (!panelOpen && !galleryOpen) return;

        const handleDocMouseDown = (event) => {
            const target = event.target instanceof Element ? event.target : null;
            if (wrapperRef.current?.contains(target)) return;
            if (target?.closest('[data-free-canvas-panel-root="true"]')) return;
            if (target?.closest('[data-free-canvas-gallery-root="true"]')) return;
            if (target?.closest('.ant-select-dropdown')) return;
            if (target?.closest('.ant-modal-root') || target?.closest('.ant-modal-wrap') || target?.closest('.ant-modal')) return;
            reopenOverlayRef.current = '';
            setPanelOpen(false);
            setGalleryOpen(false);
        };

        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleDocMouseDown);
        }, 0);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleDocMouseDown);
        };
    }, [galleryOpen, panelOpen]);

    return (
        <div
            ref={wrapperRef}
            className={`${styles.wrapper} ${galleryOpen ? styles.wrapperGalleryOpen : ''}`}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            onMouseMove={handleMouseMove}
        >
            <div
                className={`${styles.sideCapture} ${styles.sideCaptureLeft}`}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                onMouseMove={handleMouseMove}
                onMouseDown={handleLeftCaptureMouseDown}
                onClick={handleLeftCaptureMouseDown}
                aria-hidden="true"
            />
            <div
                className={`${styles.sideCapture} ${styles.sideCaptureRight}`}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                onMouseMove={handleMouseMove}
                aria-hidden="true"
            />

            <div className={`${styles.labelRow} nodrag nopan`}>
                {data.imageSequenceLabel ? (
                    <div className={styles.label}>
                        {editingSequenceLabel ? (
                            <input
                                ref={sequenceInputRef}
                                value={draftSequenceLabel}
                                maxLength={TITLE_MAX_LENGTH}
                                className={styles.labelInput}
                                onChange={(event) => setDraftSequenceLabel(event.target.value.replace(/\s+/g, ''))}
                                onBlur={commitSequenceLabelChange}
                                onKeyDown={handleSequenceLabelKeyDown}
                                onClick={(event) => event.stopPropagation()}
                            />
                        ) : (
                            <button
                                type="button"
                                className={styles.sequenceLabelButton}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    if (readOnlyView || interactionLocked) return;
                                    setEditingSequenceLabel(true);
                                }}
                            >
                                <span className={styles.imageSequenceOutside}>{data.imageSequenceLabel}</span>
                            </button>
                        )}
                    </div>
                ) : <div />}
                <AddNodePopover
                    placement="bottomRight"
                    nodeGroups={FUNCTION_NODE_GROUPS}
                    showImageItem={false}
                    title="切换功能"
                    disabled={executionStatus === 'processing' || interactionLocked}
                    onSelect={handleLabelChange}
                >
                    <div
                        className={`${styles.labelInner} ${(executionStatus === 'processing' || interactionLocked) ? styles.labelInnerDisabled : ''}`}
                        onClick={(event) => {
                            event.stopPropagation();
                            setPanelOpen(false);
                        }}
                    >
                        <span>{data.label}</span>
                        <CaretDownOutlined className={styles.labelArrow} />
                    </div>
                </AddNodePopover>
            </div>

            <Handle
                type="target"
                position={Position.Left}
                isConnectableStart={false}
                isConnectableEnd={!interactionLocked}
                onMouseDown={handleConnectionHandleMouseDown}
                onTouchStart={handleConnectionHandleMouseDown}
                className={`${handleClassName} ${styles.targetHandle}`}
                style={{
                    ...HANDLE_BASE_STYLE,
                    left: -40,
                    transform: `translateY(-50%) translate(${leftOffset.x}px, ${leftOffset.y}px)`,
                }}
            >
                <span
                    className={styles.handleIcon}
                    aria-hidden="true"
                    style={{ '--handle-icon-url': `url(${HANDLE_ICON})` }}
                />
            </Handle>

            <AddNodePopover
                placement="rightTop"
                nodeGroups={FUNCTION_NODE_GROUPS}
                showImageItem={false}
                title="引用该节点生成"
                onSelect={(nodeType) => {
                    if (interactionLocked) return;
                    data.onQuickAddNode?.(id, nodeType);
                }}
            >
                <Handle
                    type="source"
                    position={Position.Right}
                    isConnectableStart={!interactionLocked}
                    isConnectableEnd={false}
                    onMouseDown={handleConnectionHandleMouseDown}
                    onTouchStart={handleConnectionHandleMouseDown}
                    className={handleClassName}
                    style={{
                        ...HANDLE_BASE_STYLE,
                        right: -40,
                        left: 'auto',
                        transform: `translateY(-50%) translate(${rightOffset.x}px, ${rightOffset.y}px)`,
                    }}
                >
                    <span
                        className={styles.handleIcon}
                        aria-hidden="true"
                        style={{ '--handle-icon-url': `url(${HANDLE_ICON})` }}
                    />
                </Handle>
            </AddNodePopover>

            {displayMode === 'stacked' ? (
                <div className={styles.stackedShell} aria-hidden="true">
                    {stackedBackResults.map((result) => (
                        <div
                            key={result.id}
                            className={styles.stackedShellLayer}
                            style={{ '--shell-order': result.layerOrder }}
                        >
                            <ResultArtwork src={result.src} title={result.title} />
                        </div>
                    ))}
                </div>
            ) : null}

            {displayMode === 'stacked' && galleryOpen && galleryPosition ? createPortal((
                <div
                    className={`${styles.galleryPanel} ${orderedResults.length === 1 ? styles.galleryPanelSingle : ''}`}
                    data-free-canvas-gallery-root="true"
                    style={{
                        position: 'fixed',
                        top: galleryPosition.top,
                        left: galleryPosition.left,
                        gridTemplateColumns: `repeat(${galleryColumnCount}, 240px)`,
                    }}
                >
                    {orderedResults.map((result) => (
                        <div
                            key={result.id}
                            className={`${styles.galleryItem} ${result.index === resolvedSelectedResultIndex ? styles.galleryItemActive : ''}`}
                        >
                            <div className={styles.galleryThumb}>
                                <button
                                    type="button"
                                    className={styles.galleryPreviewBtn}
                                    onClick={(event) => handlePreviewResult(event, result.index)}
                                >
                                    <ResultArtwork src={result.src} title={result.title} />
                                </button>
                                <div className={styles.galleryActions}>
                                    <button
                                        type="button"
                                        className={styles.galleryActionBtn}
                                        onClick={(event) => handleDownloadResult(event, result.id, result.originalUrl)}
                                    >
                                        <img src={DOWNLOAD_ICON} alt="" width={16} height={16} draggable={false} />
                                    </button>
                                </div>
                                {!readOnlyView ? (
                                    <button
                                        type="button"
                                        className={styles.gallerySelectBtn}
                                        onClick={(event) => handleResultSelect(event, result.index)}
                                    >
                                        {isManualSelectionPending ? '选这张开始生成' : (result.index === resolvedSelectedResultIndex ? '已选中' : '选这张')}
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    ))}
                </div>
            ), document.body) : null}

            <Popover
                open={panelOpen}
                onOpenChange={setPanelOpen}
                content={PanelComponent ? (
                    <div data-free-canvas-panel-root="true">
                        <PanelComponent
                            panelKey={`${id}:${data.functionKey || ''}`}
                            nodeId={id}
                            nodeData={data}
                            onChange={data.onChange}
                            onClose={handlePanelClose}
                            disabled={groupExecutionLocked || readOnlyView}
                        />
                    </div>
                ) : null}
                trigger={[]}
                placement="rightTop"
                arrow={false}
                destroyOnHidden
                overlayInnerStyle={{ padding: 0 }}
                getPopupContainer={() => document.body}
            >
                <div
                    className={`${styles.card} ${selected ? styles.cardSelected : ''} ${PanelComponent ? styles.cardClickable : ''} ${(displayMode === 'stacked' && !selected) ? styles.cardTransparentBorder : ''}`}
                    onClick={handleCardClick}
                >
                    {displayMode === 'stacked' && activeResult ? (
                        <>
                            <ResultArtwork src={activeResult.src} title={activeResult.title} />
                            {isManualSelectionPending ? (
                                <button
                                    type="button"
                                    className={`${styles.manualSelectButton} nodrag nopan`}
                                    onClick={handleManualSelectClick}
                                >
                                    待选图
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className={`${styles.galleryOpenButton} nodrag nopan`}
                                    onClick={handleGalleryToggle}
                                >
                                    查看图集
                                </button>
                            )}
                        </>
                    ) : (
                        <div className={styles.placeholder}>
                            {executionStatus === 'processing' ? (
                                <video
                                    className={styles.processingVideo}
                                    src={PROCESSING_VIDEO}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                />
                            ) : (
                                <img
                                    src={PLACEHOLDER_ICON}
                                    alt=""
                                    width={24}
                                    height={24}
                                    draggable={false}
                                    className={executionStatus === 'failed' ? styles.placeholderIconFailed : ''}
                                />
                            )}
                            <span className={`${styles.displayModeText} ${executionStatus === 'failed' ? styles.displayModeTextFailed : ''}`}>
                                {executionStatus === 'processing'
                                    ? '生成中'
                                    : executionStatus === 'failed'
                                        ? '生成失败'
                                        : '待生成'}
                            </span>
                        </div>
                    )}
                </div>
            </Popover>

            <Image.PreviewGroup
                preview={{
                    visible: previewImageIndex >= 0,
                    current: previewImageIndex >= 0 ? previewImageIndex : 0,
                    toolbarRender: renderPreviewToolbar,
                    onVisibleChange: (visible) => {
                        if (!visible) setPreviewImageIndex(-1);
                    },
                    onChange: (current) => {
                        setPreviewImageIndex(current);
                    },
                }}
                items={orderedResults.map((item) => item.originalUrl)}
            />
        </div>
    );
};

export default FunctionNode;
